/**
 * Template Service
 * Handles user template management and storage
 */
import { webStorageService } from './webStorageService';
import { cognitoAuthService } from './cognitoAuthService';
import { templateApiService } from './templateApiService';

class TemplateService {
  constructor() {
    this.storageKeys = {
      templates: 'user_templates',
      defaultTemplate: 'default_template'
    };

    // Note types and their corresponding AI prompts
    this.noteTypes = {
      'progress': {
        label: 'Progress Note',
        prompt: 'Convert this session summary into a comprehensive clinical progress note following SOAP format. Include: Subjective observations from the patient, Objective clinical findings and behaviors observed, Assessment of current mental status and progress toward goals, and Plan for continued treatment including interventions and next steps. Use professional clinical language appropriate for medical records.'
      },
      'intake': {
        label: 'Intake Document', 
        prompt: 'Structure this information as a comprehensive intake assessment including: presenting problem and chief complaint, relevant history (medical, psychiatric, social, family), mental status examination findings, risk assessment (suicide, homicide, substance use), diagnostic impressions with supporting evidence, and detailed treatment recommendations. Use thorough clinical documentation standards.'
      },
      'termination': {
        label: 'Termination Note',
        prompt: 'Create a comprehensive termination summary including: treatment goals achieved and outcomes, progress made during therapy, current mental status and functioning level, remaining challenges or areas for continued focus, discharge recommendations and referrals if needed, and prognosis. Summarize the entire treatment episode professionally.'
      }
    };
  }

  /**
   * Ensure user context is set for storage operations
   */
  ensureUserContext() {
    const currentUser = cognitoAuthService.getCurrentUser();
    if (currentUser && currentUser.id) {
      webStorageService.setCurrentUser(currentUser.id);
    }
  }

  /**
   * Get available note types
   * @returns {Object} Note types with labels and prompts
   */
  getNoteTypes() {
    return this.noteTypes;
  }

  /**
   * Get AI prompt for a specific note type
   * @param {string} noteType - The note type key
   * @returns {string} AI prompt for the note type
   */
  getAIPromptForNoteType(noteType) {
    if (!noteType) return this.noteTypes['progress'].prompt;

    // Exact key match (legacy support)
    if (this.noteTypes[noteType]) return this.noteTypes[noteType].prompt;

    // Fuzzy match on the human-readable label string
    const lower = noteType.toLowerCase();
    if (lower.includes('progress') || lower.includes('session')) return this.noteTypes['progress'].prompt;
    if (lower.includes('intake') || lower.includes('assessment') || lower.includes('evaluation') || lower.includes('initial')) return this.noteTypes['intake'].prompt;
    if (lower.includes('terminat') || lower.includes('discharge') || lower.includes('summary') || lower.includes('closing')) return this.noteTypes['termination'].prompt;

    // Custom template type — build a context-aware generic prompt from the name
    return `Convert this therapist voice note into a professional, HIPAA-compliant clinical document formatted as a ${noteType}. Use clinical language appropriate for medical records and the specific documentation context implied by the template name. Organize content clearly into the provided template sections.`;
  }

  /**
   * Get all user templates
   * @returns {Array} Array of template objects
   */
  async getUserTemplates() {
    try {
      this.ensureUserContext();
      const templates = await webStorageService.storage.get(this.storageKeys.templates) || [];
      return templates;
    } catch (error) {
      console.error('Failed to get user templates:', error);
      return [];
    }
  }

  /**
   * Get the default template ID
   * @returns {string} Default template ID
   */
  async getDefaultTemplateId() {
    try {
      this.ensureUserContext();
      return await webStorageService.storage.get(this.storageKeys.defaultTemplate) || '';
    } catch (error) {
      console.error('Failed to get default template:', error);
      return '';
    }
  }

  /**
   * Get the default template object
   * @returns {Object|null} Default template object
   */
  async getDefaultTemplate() {
    try {
      const templates = await this.getUserTemplates();
      const defaultId = await this.getDefaultTemplateId();
      
      if (defaultId) {
        const defaultTemplate = templates.find(t => t.id === defaultId);
        if (defaultTemplate) return defaultTemplate;
      }
      
      // If no default set, return first template
      return templates.length > 0 ? templates[0] : null;
    } catch (error) {
      console.error('Failed to get default template:', error);
      return null;
    }
  }

  /**
   * Get template by ID
   * @param {string} templateId - Template ID
   * @returns {Object|null} Template object
   */
  async getTemplateById(templateId) {
    try {
      const templates = await this.getUserTemplates();
      return templates.find(t => t.id === templateId) || null;
    } catch (error) {
      console.error('Failed to get template by ID:', error);
      return null;
    }
  }

  /**
   * Save user templates
   * @param {Array} templates - Array of template objects
   * @returns {boolean} Success status
   */
  async saveUserTemplates(templates) {
    try {
      this.ensureUserContext();
      const success = await webStorageService.storage.set(this.storageKeys.templates, templates);

      // Fire-and-forget cloud push
      this._pushAllToCloud(templates);

      return success;
    } catch (error) {
      console.error('Failed to save templates:', error);
      return false;
    }
  }

  /**
   * Set default template ID
   * @param {string} templateId - Template ID to set as default
   * @returns {boolean} Success status
   */
  async setDefaultTemplate(templateId) {
    try {
      this.ensureUserContext();
      return await webStorageService.storage.set(this.storageKeys.defaultTemplate, templateId);
    } catch (error) {
      console.error('Failed to set default template:', error);
      return false;
    }
  }

  /**
   * Create a new template
   * @param {Object} templateData - Template data
   * @returns {Object|null} Created template object
   */
  async createTemplate(templateData) {
    try {
      const newTemplate = {
        id: `template_${Date.now()}`,
        name: templateData.name.trim(),
        noteType: templateData.noteType || 'progress',
        fields: templateData.fields.filter(field => field.trim() !== '').map(field => field.trim()),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const templates = await this.getUserTemplates();
      const updatedTemplates = [...templates, newTemplate];
      
      const success = await this.saveUserTemplates(updatedTemplates);
      if (success) {
        return newTemplate;
      }
      return null;
    } catch (error) {
      console.error('Failed to create template:', error);
      return null;
    }
  }

  /**
   * Update an existing template
   * @param {string} templateId - Template ID to update
   * @param {Object} templateData - Updated template data
   * @returns {Object|null} Updated template object
   */
  async updateTemplate(templateId, templateData) {
    try {
      const templates = await this.getUserTemplates();
      const templateIndex = templates.findIndex(t => t.id === templateId);
      
      if (templateIndex === -1) return null;

      const updatedTemplate = {
        ...templates[templateIndex],
        name: templateData.name.trim(),
        noteType: templateData.noteType || templates[templateIndex].noteType || 'progress',
        fields: templateData.fields.filter(field => field.trim() !== '').map(field => field.trim()),
        updatedAt: new Date().toISOString()
      };

      templates[templateIndex] = updatedTemplate;
      
      const success = await this.saveUserTemplates(templates);
      if (success) {
        return updatedTemplate;
      }
      return null;
    } catch (error) {
      console.error('Failed to update template:', error);
      return null;
    }
  }

  /**
   * Delete a template
   * @param {string} templateId - Template ID to delete
   * @returns {boolean} Success status
   */
  async deleteTemplate(templateId) {
    try {
      const templates = await this.getUserTemplates();
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      
      // If deleting the default template, clear the default
      const defaultId = await this.getDefaultTemplateId();
      if (templateId === defaultId) {
        const newDefault = updatedTemplates.length > 0 ? updatedTemplates[0].id : '';
        await this.setDefaultTemplate(newDefault);
      }

      const success = await this.saveUserTemplates(updatedTemplates);

      // Deletion must be explicit: _pushAllToCloud is upsert-only and will NOT
      // remove this record from the cloud, so tell the cloud directly.
      templateApiService.deleteTemplate(templateId).catch(err =>
        console.warn('Cloud delete failed for template:', templateId, err)
      );

      return success;
    } catch (error) {
      console.error('Failed to delete template:', error);
      return false;
    }
  }

  /**
   * Initialize default templates if none exist
   * @returns {boolean} Success status
   */
  async initializeDefaultTemplates() {
    try {
      const existingTemplates = await this.getUserTemplates();
      if (existingTemplates.length > 0) return true; // Already has templates

      // Create default templates
      const defaultTemplates = [
        {
          id: 'template_default_1',
          name: 'Progress/Intervention/Response',
          fields: ['Progress', 'Intervention', 'Response'],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'template_default_2',
          name: 'SOAP Note',
          fields: ['Subjective', 'Objective', 'Assessment', 'Plan'],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

      const success = await this.saveUserTemplates(defaultTemplates);
      if (success) {
        await this.setDefaultTemplate('template_default_1');
      }
      
      return success;
    } catch (error) {
      console.error('Failed to initialize default templates:', error);
      return false;
    }
  }

  /**
   * Export templates to file
   * @returns {string} JSON string of templates
   */
  async exportTemplates() {
    try {
      const templates = await this.getUserTemplates();
      const defaultId = await this.getDefaultTemplateId();
      
      const exportData = {
        templates,
        defaultTemplate: defaultId,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      };
      
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Failed to export templates:', error);
      return null;
    }
  }

  /**
   * Import templates from file
   * @param {string} jsonData - JSON string of templates
   * @returns {boolean} Success status
   */
  async importTemplates(jsonData) {
    try {
      const importData = JSON.parse(jsonData);
      
      if (!importData.templates || !Array.isArray(importData.templates)) {
        throw new Error('Invalid template data format');
      }

      // Validate template structure
      const validTemplates = importData.templates.filter(template => 
        template.name && 
        template.fields && 
        Array.isArray(template.fields) && 
        template.fields.length >= 2
      );

      if (validTemplates.length === 0) {
        throw new Error('No valid templates found in import data');
      }

      // Assign new IDs to avoid conflicts
      const templatesWithNewIds = validTemplates.map(template => ({
        ...template,
        id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }));

      const success = await this.saveUserTemplates(templatesWithNewIds);
      if (success && templatesWithNewIds.length > 0) {
        await this.setDefaultTemplate(templatesWithNewIds[0].id);
      }

      return success;
    } catch (error) {
      console.error('Failed to import templates:', error);
      return false;
    }
  }
  // ===== Cloud Sync Methods =====

  /**
   * Push local templates to the cloud as UPSERTS only.
   *
   * This method is intentionally non-destructive: it never deletes a cloud
   * template just because it is missing locally. The previous "reconcile" logic
   * treated a single browser's localStorage as the source of truth and deleted
   * every cloud template it didn't recognize — which, combined with ID drift and
   * transient fetch errors, wiped users' real templates. Deletions are now driven
   * only by explicit user action in deleteTemplate().
   *
   * We send each template's id as `templateId` so the server stores it under the
   * SAME id. That keeps local and cloud ids identical, so re-pushing updates the
   * existing record instead of creating duplicates.
   *
   * @param {Array} templates - Local templates to upsert into the cloud
   */
  async _pushAllToCloud(templates) {
    try {
      const cloudTemplates = await templateApiService.fetchTemplates();
      const cloudIds = new Set(cloudTemplates.map(t => t.templateId));

      for (const t of templates) {
        const payload = {
          templateId: t.id,
          name: t.name,
          noteType: t.noteType || 'progress',
          fields: t.fields
        };
        if (cloudIds.has(t.id)) {
          await templateApiService.updateTemplate(t.id, payload);
        } else {
          await templateApiService.createTemplate(payload);
        }
      }
    } catch (error) {
      // fetchTemplates throws when the cloud is unreachable; do nothing
      // destructive — local stays intact and will re-sync later.
      console.warn('Cloud push failed for templates:', error);
    }
  }

  /**
   * Pull templates from the cloud into local storage.
   *
   * @returns {Promise<{ok: boolean, templates: Array|null}>}
   *   ok=false  -> cloud was unreachable. Caller MUST NOT initialize defaults or
   *               push, because doing so could clobber real (but unread) cloud data.
   *   ok=true, templates=[] -> cloud is reachable and genuinely empty.
   *   ok=true, templates=[...] -> cloud data, already written to local storage.
   */
  async syncFromCloud() {
    let cloudTemplates;
    try {
      cloudTemplates = await templateApiService.fetchTemplates();
    } catch (error) {
      console.warn('Cloud sync unavailable for templates:', error);
      return { ok: false, templates: null };
    }

    if (!cloudTemplates || cloudTemplates.length === 0) {
      // Cloud is reachable but empty — push local templates up if they exist.
      const localTemplates = await this.getUserTemplates();
      if (localTemplates.length > 0) {
        this._pushAllToCloud(localTemplates);
      }
      return { ok: true, templates: [] };
    }

    // Cloud has templates — convert to local format and write to localStorage
    const localFormat = cloudTemplates.map(ct => ({
      id: ct.templateId,
      name: ct.name,
      noteType: ct.noteType || "",
      fields: ct.fields || [],
      isDefault: ct.isDefault || false,
      createdAt: ct.createdAt,
      updatedAt: ct.updatedAt
    }));

    this.ensureUserContext();
    await webStorageService.storage.set(this.storageKeys.templates, localFormat);

    const defaultTemplate = cloudTemplates.find(t => t.isDefault);
    if (defaultTemplate) {
      await webStorageService.storage.set(this.storageKeys.defaultTemplate, defaultTemplate.templateId);
    }

    return { ok: true, templates: localFormat };
  }

}

// Create and export singleton instance
export const templateService = new TemplateService();
export default templateService;
